<template>
  <div :style="settingsStore.colorSetup" class="webkit-fill-fix">
    <template v-if="ifShareHasExpired">
      <main class="flex h-screen border-box justify-center items-center bg-gray-100">
        <div class="bg-white border-gray-200 border w-1/2 text-center p-12 rounded-lg">
          <h2 class="text-20 text-black-60% font-semibold">{{ $t("Uchi.Share.Expired") }}</h2>
          <p class="text-black-60%">{{ $t("Uchi.Share.ExpiredSub") }}</p>
        </div>
      </main>
    </template>
    <template v-if="isPasswordRequired">
      <main class="flex h-screen border-box justify-center items-center bg-gray-100">
        <div class="bg-white border-gray-200 border w-1/2 text-center p-12 rounded-lg">
          <h2 class="text-20 text-black-60% font-semibold mb-3">{{ $t("Uchi.Password.SubmitPassword") }}</h2>
          <p class="text-12 text-black-60% mb-6">{{ $t("Uchi.Password.SubMessage") }}</p>
          <input
            v-model="password"
            class="w-60 border-t-0 border-l-0 border-r-0 border-orange-400 border-2 p-2 mb-2 text-center"
            type="password"
          />
          <div v-if="errorMessage" class="text-red-500 mt-3">
            {{ errorMessage }}
          </div>
          <div>
            <button class="button button-primary mt-6" @click="validatePassword">OK</button>
          </div>
          <div class="float-right mt-10">
            <button class="text-black-60% text-xs" :disabled="isSending" @click="resendNewPassword">
              {{ isSending ? $t("Uchi.Password.NewPasswordSend") : $t("Uchi.Password.ResendPassword") }}
            </button>
          </div>
        </div>
      </main>
    </template>
    <template v-if="!ifShareHasExpired">
      <div class="flex flex-col h-screen border-box">
        <TopPanel
          :is-institution-selector-visible="false"
          :is-user-info-visible="false"
          class="absolute inset-x-0 top-0 h-16 md:h-24"
        />

        <div class="flex-grow flex pt-16 md:pt-24 webkit-fill-fix">
          <LeftPanel :is-left-menu-visible="false" :is-document-search-visible="false" />
          <main v-if="!loading" id="main-content" class="flex-grow p-4 md:p-8 overflow-auto pb-20 md:pb-4">
            <router-view />
          </main>
        </div>
      </div>
      <transition name="fade">
        <div v-if="loading" id="preloader" class="flex items-center justify-center">
          <InfoSpinner></InfoSpinner>
        </div>
      </transition>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import LeftPanel from "@/components/Navigation/LeftPanel";
import TopPanel from "@/components/Navigation/TopPanel";
import InfoSpinner from "@/components/Shared/InfoSpinner.vue";

import { useSettingsStore } from "@/stores/settings";
import { useDocumentStore } from "@/stores/documents";

defineOptions({ name: "ShareApp" });

const route = useRoute();
const { t } = useI18n();
const settingsStore = useSettingsStore();
const documentStore = useDocumentStore();

const loading = ref(true);
const isPasswordRequired = ref(false);
const password = ref("");
const errorMessage = ref("");
const ifShareHasExpired = ref(false);
const isSending = ref(false);

const validatePassword = async () => {
  errorMessage.value = "";
  try {
    const result = await settingsStore.validatePasswordOnQuickShare(route.params.shareId, password.value);

    if (!result) {
      errorMessage.value = t("Uchi.Password.ErrorMessage");
      return;
    }

    await documentStore.loadQuickShareDocuments();
    isPasswordRequired.value = false;
  } catch {
    errorMessage.value = t("Uchi.Password.ErrorMessage");
  }
};

const resendNewPassword = async () => {
  isSending.value = true;
  try {
    if (route.params.shareId) {
      setTimeout(() => {
        isSending.value = false;
      }, 2000);
      await settingsStore.resendPasswordOnQuickShare(route.params.shareId);
    }
  } catch {
    /* empty */
  }
};

const init = async () => {
  try {
    loading.value = true;
    if (route.params.shareId) {
      await settingsStore.loadQuickShareFromKey(route.params.shareId);
    }

    ifShareHasExpired.value = false;
  } catch {
    ifShareHasExpired.value = true;
  } finally {
    if (
      !settingsStore.quickShareSessionToken ||
      settingsStore.quickShareSessionToken === "00000000-0000-0000-0000-000000000000"
    ) {
      isPasswordRequired.value = true;
    }
  }

  loading.value = false;
};

init();
</script>
